/**
 * Report brand guidelines — the house rules every produced document is set in.
 *
 * Two profiles because the media differ: `pdf` is the print system's identity,
 * `xlsx` is a workbook's. `output` carries the per-report-type defaults.
 *
 * Only the marks that carry identity are configurable. Body ink and the type
 * scale belong to the print system and are not exposed, so a desk cannot
 * accidentally produce a document that is no longer its own.
 */
import api from './api'
import type { ReportTypeId } from './reportDefinitions'

export type PdfBrandProfile = {
  accent: string
  accentDeep: string
  accentPale: string
  display: string
  displayDeep: string
  hairline: string
  rule: string
  tableHeadBg: string
  tableHeadText: string
  zebra: boolean
  zebraBg: string
  pageSize: 'letter' | 'letter-landscape' | 'legal-landscape' | 'tabloid-landscape' | 'a4'
  fontFamily: 'helvetica' | 'times'
  tableRules: 'ruled' | 'banded' | 'boxed'
  logoEnabled: boolean
  coverLogoEnabled: boolean
  confidentialityLine: string
  footerCentre: string
}

export type XlsxBrandProfile = {
  headerBg: string
  headerText: string
  rowBg: string
  rowAltBg: string
  rowText: string
  gridBorder: string
  zebra: boolean
  gridlines: boolean
  freezeHeader: boolean
  logoEnabled: boolean
  numberStyle: 'accounting' | 'plain'
  currencyFormat: string
  percentFormat: string
  multipleFormat: string
  dateFormat: string
  fitToWidth: boolean
  repeatHeaderRow: boolean
}

export type ReportOutputDefaults = {
  defaultFormat: Record<ReportTypeId, 'pdf' | 'xlsx'>
  retainArtifacts: boolean
  retentionDays: number
}

export type ReportBranding = {
  pdf: PdfBrandProfile
  xlsx: XlsxBrandProfile
  output: ReportOutputDefaults
}

/** A partial save: the API merges section by section, so sending only the PDF
 *  profile never clears the workbook one. */
export type ReportBrandingPatch = {
  pdf?: Partial<PdfBrandProfile>
  xlsx?: Partial<XlsxBrandProfile>
  output?: Partial<ReportOutputDefaults>
}

export const reportBrandingService = {
  get: () => api.get<ReportBranding>('/reports/branding').then((r) => r.data),
  update: (body: ReportBrandingPatch) =>
    api.put<ReportBranding>('/reports/branding', body).then((r) => r.data),
}
